import React, { useState, useEffect, useCallback, useRef } from 'react';
import { db, auth } from '../firebase';
import { collection, getDocs, doc, runTransaction, serverTimestamp, query, updateDoc, where, setDoc, deleteDoc, getDoc } from 'firebase/firestore';
import '../GestionUsuarios.css'; // Reutilizar estilos
import { abrirReporteRecibo } from './SicReporteRecibos'; // Importar la función para abrir el reporte
import EditModal from './EditModal'; // Reutilizar el modal de edición de clientes
import numeroALetras from '../utils/numeroALetras.js';

// Componente para la búsqueda de clientes en un modal (reutilizado de SicCotizaciones)
const ClienteSearchModal = ({ onClose, onSelect, isAdmin }) => {
    const [clientes, setClientes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [editingCliente, setEditingCliente] = useState(null);

    const fetchClientes = useCallback(async () => {
        setLoading(true);
        try {
            const clientesCollection = collection(db, 'SicTablaClientes');
            const clientesSnapshot = await getDocs(clientesCollection);
            const clientesList = clientesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setClientes(clientesList);
        } catch (error) {
            console.error("Error al obtener clientes: ", error);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchClientes();
    }, [fetchClientes]);

    const filteredClientes = clientes.filter(cliente =>
        cliente.nombre?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        cliente.cedula?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const handleSelect = (cliente) => {
        onSelect(cliente);
        onClose();
    };

    const handleEdit = (cliente) => {
        setEditingCliente(cliente);
        setIsEditModalOpen(true);
    };

    const handleDelete = async (cliente) => {
        if (!cliente || !cliente.id) return;
        const confirmado = window.confirm(`¿Está seguro que desea eliminar al cliente: ${cliente.nombre} (${cliente.id})?`);
        if (!confirmado) return;
        try {
            await deleteDoc(doc(db, 'SicTablaClientes', cliente.id));
            await fetchClientes();
            alert(`✅ Cliente ${cliente.nombre} eliminado correctamente.`);
        } catch (error) {
            console.error('Error al eliminar cliente:', error);
            alert('❌ Ocurrió un error al eliminar el cliente.');
        }
    };

    const handleCreate = () => {
        setEditingCliente(null);
        setIsEditModalOpen(true);
    };

    const handleSaveEdit = async (recordData) => {
        try {
            const currentUserEmail = auth.currentUser?.email || '';
            if (recordData && recordData.id) {
                // Edición de cliente existente
                const recordRef = doc(db, 'SicTablaClientes', recordData.id);
                await updateDoc(recordRef, {
                    nombre: recordData.nombre || '',
                    correo: recordData.correo || '',
                    telefonoOficina: recordData.telefonoOficina || '',
                    telefonoCelular: recordData.telefonoCelular || '',
                    responsable: currentUserEmail,
                    fechaModificacion: serverTimestamp(),
                });
            } else {
                // Creación de cliente nuevo con ID = cédula
                const cedula = (recordData.cedula || '').trim();
                if (!cedula) {
                    throw new Error('La cédula es obligatoria para crear el cliente.');
                }
                const nuevoDocRef = doc(db, 'SicTablaClientes', cedula);
                await setDoc(nuevoDocRef, {
                    cedula: cedula,
                    nombre: recordData.nombre || '',
                    correo: recordData.correo || '',
                    telefonoOficina: recordData.telefonoOficina || '',
                    telefonoCelular: recordData.telefonoCelular || '',
                    responsable: currentUserEmail,
                    fechaCreacion: serverTimestamp(),
                    fechaModificacion: serverTimestamp(),
                }, { merge: true });
            }
            await fetchClientes(); // Recargar la lista de clientes
            setIsEditModalOpen(false);
            setEditingCliente(null);
        } catch (error) {
            console.error("Error al guardar/actualizar el cliente: ", error);
            alert("Error al guardar los cambios del cliente.");
        }
    };

    return (
        <>
            <div className="modal-overlay" onClick={onClose}>
                <div className="modal-content" onClick={e => e.stopPropagation()} style={{ width: '80%', maxWidth: '900px' }}>
                    <div className="modal-header">
                        <h2>Buscar Cliente</h2>
                        <button onClick={onClose} className="modal-close">×</button>
                    </div>
                    <div className="modal-body">
                        <div className="search-container" style={{ display: 'flex', alignItems: 'center', gap: '1rem', padding: '0 1rem', marginBottom: '1rem' }}>
                            <input
                                type="text"
                                placeholder="Buscar por nombre o cédula..."
                                className="form-control"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                style={{ flex: 1, padding: '0.75rem' }}
                            />
                            <div className="tooltip-container">
                                <button 
                                    className="btn btn-primary icon-btn"
                                    onClick={handleCreate}
                                    style={{ 
                                        padding: '0.5rem 1rem',
                                        backgroundColor: '#28a745', 
                                        color: 'white',
                                        fontSize: '1.1rem'
                                    }}
                                >
                                    ➕
                                </button>
                                <span className="tooltip-text">Crear Cliente</span>
                            </div>
                        </div>
                        {loading ? <p>Cargando...</p> : (
                            <div className="table-responsive" style={{ maxHeight: '400px' }}>
                                <table className="usuarios-table" style={{ width: '100%' }}>
                                    <thead>
                                        <tr>
                                            <th style={{ width: '65%' }}>Nombre</th>
                                            <th style={{ width: '15%' }}>Cédula</th>
                                            <th style={{ width: '20%' }}>Acciones</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredClientes.map(cliente => (
                                            <tr key={cliente.id}>
                                                <td>
                                                    <input
                                                        type="text"
                                                        value={cliente.nombre || ''}
                                                        className="form-control"
                                                        readOnly
                                                        style={{ 
                                                            backgroundColor: '#f8f9fa', 
                                                            border: '1px solid #dee2e6',
                                                            width: '100%'
                                                        }}
                                                    />
                                                </td>
                                                <td>
                                                    <input
                                                        type="text"
                                                        value={cliente.cedula || ''}
                                                        className="form-control"
                                                        readOnly
                                                        style={{ 
                                                            backgroundColor: '#f8f9fa', 
                                                            border: '1px solid #dee2e6',
                                                            width: '100%'
                                                        }}
                                                    />
                                                </td>
                                                <td className="actions-cell">
                                                    <div style={{ display: 'flex', gap: '5px', justifyContent: 'center' }}>
                                                        <div className="tooltip-container">
                                                            <button 
                                                                className="icon-btn success" 
                                                                onClick={() => handleSelect(cliente)}
                                                                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '5px' }}
                                                            >
                                                                ✅
                                                            </button>
                                                            <span className="tooltip-text">Seleccionar</span>
                                                        </div>
                                                        <div className="tooltip-container">
                                                            <button 
                                                                className="icon-btn warning" 
                                                                onClick={() => handleEdit(cliente)}
                                                                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '5px' }}
                                                            >
                                                                ✏️
                                                            </button>
                                                            <span className="tooltip-text">Editar</span>
                                                        </div>
                                                        {isAdmin && (
                                                            <div className="tooltip-container">
                                                                <button
                                                                    className="icon-btn danger"
                                                                    onClick={() => handleDelete(cliente)}
                                                                    style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '5px' }}
                                                                >
                                                                    🗑️
                                                                </button>
                                                                <span className="tooltip-text">Eliminar</span>
                                                            </div>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </div>
            </div>
            {isEditModalOpen && (
                <EditModal
                    record={editingCliente}
                    onClose={() => setIsEditModalOpen(false)}
                    onSave={handleSaveEdit}
                    type="Cliente"
                />
            )}
        </>
    );
};

const getTodayFormatted = () => {
    const fecha = new Date();
    const dias = ['domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado'];
    const meses = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];
    return `${dias[fecha.getDay()]}, ${fecha.getDate()} de ${meses[fecha.getMonth()]} de ${fecha.getFullYear()}`;
};

const SicRecibos = () => {
    const [reciboData, setReciboData] = useState({
        numeroRecibo: '',
        fechaRecibo: new Date(),
        cliente: '',
        moneda: 'Colones',
        monto: 0,
        cancelaCuenta: false,
        montoEnLetras: '',
        concepto: '',
        formaDePago: 'Sinpe Móvil',
        banco: '',
        numeroDeDocumento: '',
        saldoAnterior: 0,
        esteAbono: 0,
        saldoActual: 0,
        hechoPor: '',
    });

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [message, setMessage] = useState('');
    const [isEditing, setIsEditing] = useState(false);
    const [isClienteModalOpen, setIsClienteModalOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [isSearchModalOpen, setIsSearchModalOpen] = useState(false);
    const [allRecibos, setAllRecibos] = useState([]);
    const [isAdmin, setIsAdmin] = useState(false);
    const [currentUserName, setCurrentUserName] = useState(''); // Nuevo estado para el nombre del usuario

    const toolbarRef = useRef(null);
    const [isToolbarFixed, setIsToolbarFixed] = useState(false);
    const [toolbarHeight, setToolbarHeight] = useState(0);
    const [headerHeight, setHeaderHeight] = useState(0);

    // Effect to update montoEnLetras when monto or moneda changes
    useEffect(() => {
        setReciboData(prev => ({
            ...prev,
            montoEnLetras: numeroALetras(prev.monto, prev.moneda)
        }));
    }, [reciboData.monto, reciboData.moneda]);

    // Effect to automatically set 'esteAbono' to the value of 'monto'
    useEffect(() => {
        setReciboData(prev => ({
            ...prev,
            esteAbono: prev.monto
        }));
    }, [reciboData.monto]);

    // Effect to update saldoActual when saldoAnterior or esteAbono changes
    useEffect(() => {
        setReciboData(prev => ({
            ...prev,
            saldoActual: (parseFloat(prev.saldoAnterior) || 0) - (parseFloat(prev.esteAbono) || 0)
        }));
    }, [reciboData.saldoAnterior, reciboData.esteAbono]);

    // Fetch user role and set hechoPor
    useEffect(() => {
        const fetchUserRole = async () => {
            const currentUser = auth.currentUser;
            if (currentUser) {
                const usersCollection = collection(db, 'usuarios');
                const q = query(usersCollection, where('email', '==', currentUser.email));
                const querySnapshot = await getDocs(q);
                if (!querySnapshot.empty) {
                    const userData = querySnapshot.docs[0].data();
                    if (userData.rol === 'admin') {
                        setIsAdmin(true);
                    }
                    if (userData.nombre) {
                        setCurrentUserName(userData.nombre);
                        setReciboData(prev => ({ ...prev, hechoPor: userData.nombre }));
                    }
                }
            }
        };
        fetchUserRole();
    }, []);

    // Toolbar sticky logic
    useEffect(() => {
        const scrollContainer = document.querySelector('.main-content');
        const appHeader = document.querySelector('.app-header');
        const updateHeaderHeight = () => setHeaderHeight(appHeader ? appHeader.offsetHeight || 0 : 0);

        const handleScrollAny = () => {
            const scrolledMain = scrollContainer ? (scrollContainer.scrollTop || 0) : 0;
            const scrolledWindow = window.pageYOffset || document.documentElement.scrollTop || 0;
            if (toolbarRef.current) {
                const currentHeight = toolbarRef.current.offsetHeight || 0;
                if (currentHeight && currentHeight !== toolbarHeight) setToolbarHeight(currentHeight);
            }
            setIsToolbarFixed(scrolledMain > 0 || scrolledWindow > 0);
        };

        updateHeaderHeight();
        handleScrollAny();

        scrollContainer?.addEventListener('scroll', handleScrollAny, { passive: true });
        window.addEventListener('scroll', handleScrollAny, { passive: true });
        window.addEventListener('resize', updateHeaderHeight, { passive: true });

        return () => {
            scrollContainer?.removeEventListener('scroll', handleScrollAny);
            window.removeEventListener('scroll', handleScrollAny);
            window.removeEventListener('resize', updateHeaderHeight);
        };
    }, [toolbarHeight]);

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setReciboData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : (type === 'number' ? parseFloat(value) || 0 : value)
        }));
    };

    const handleClienteSelect = (cliente) => {
        setReciboData(prev => ({
            ...prev,
            cliente: cliente.nombre,
        }));
    };

    const fetchAllRecibos = useCallback(async () => {
        try {
            const recibosSnapshot = await getDocs(collection(db, 'SicRecibos'));
            const recibosList = recibosSnapshot.docs.map(doc => ({
                id: doc.id,
                numeroRecibo: doc.id,
                ...doc.data()
            })).sort((a, b) => {
                const numA = parseInt(a.numeroRecibo) || 0;
                const numB = parseInt(b.numeroRecibo) || 0;
                return numA - numB;
            });
            setAllRecibos(recibosList);
        } catch (error) {
            console.error("Error al cargar recibos:", error);
        }
    }, []);

    useEffect(() => {
        fetchAllRecibos();
    }, [fetchAllRecibos]);

    const loadRecibo = async (numeroRecibo) => {
        if (!numeroRecibo) return;
        setLoading(true);
        setError('');
        setMessage('');
        try {
            const reciboRef = doc(db, 'SicRecibos', numeroRecibo);
            const reciboDoc = await getDoc(reciboRef);

            if (!reciboDoc.exists()) {
                throw new Error(`No se encontró el recibo #${numeroRecibo}`);
            }

            const data = reciboDoc.data();
            setReciboData({
                ...data,
                numeroRecibo: numeroRecibo,
                fechaRecibo: data.fechaRecibo?.toDate ? data.fechaRecibo.toDate() : new Date(),
            });
            setIsEditing(true);
            setSearchTerm('');
        } catch (err) {
            console.error("Error al cargar recibo:", err);
            setError(`Error al cargar: ${err.message}`);
            alert(`Error al cargar el recibo: ${err.message}`);
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async () => {
    setLoading(true);
    setError('');
    setMessage('');

    if (!reciboData.cliente || !reciboData.monto || !reciboData.concepto) {
        setError("Los campos 'Cliente', 'Monto' y 'Concepto' son obligatorios.");
        setLoading(false);
        return;
    }

    try {
        if (isEditing) {
            // La lógica correcta es verificar si ya existe un número de recibo.
            if (reciboData.numeroRecibo) {
                const reciboRef = doc(db, 'SicRecibos', reciboData.numeroRecibo);
                // Check if the document exists before attempting to update
                const docSnapshot = await getDoc(reciboRef);
                if (docSnapshot.exists()) {
                    await updateDoc(reciboRef, {
                        ...reciboData,
                        fechaRecibo: reciboData.fechaRecibo, // Keep original date or update if changed
                        fechaModificacion: serverTimestamp(),
                        actualizadoPor: auth.currentUser?.email,
                    });
                    setMessage(`✅ Recibo #${reciboData.numeroRecibo} actualizado exitosamente.`);
                } else {
                    setError(`Recibo #${reciboData.numeroRecibo} no encontrado.`);
                    return;
                }
            }
        } else { // Si no hay número de recibo, es uno nuevo.
            const paramsRef = doc(db, 'Parametros', 'configuracion'); // Apunta al documento de configuración
            const newReciboNum = await runTransaction(db, async (transaction) => {
                const paramDoc = await transaction.get(paramsRef);
                if (!paramDoc.exists()) {
                    throw new Error("El documento de parámetros 'configuracion' no existe. Por favor, configúralo en Herramientas > Parámetros.");
                }
                const currentNum = paramDoc.data()?.numeroRecibo || 0; // Usa el campo 'numeroRecibo'
                const nextNum = currentNum + 1;
                transaction.update(paramsRef, { numeroRecibo: nextNum }); // Actualiza el campo 'numeroRecibo'
                return String(nextNum).padStart(4, '0');
            });

            const newReciboRef = doc(db, 'SicRecibos', newReciboNum);
            await setDoc(newReciboRef, {
                ...reciboData,
                numeroRecibo: newReciboNum,
                fechaRecibo: serverTimestamp(),
                fechaCreacion: serverTimestamp(),
                    hechoPor: currentUserName, // Usar el nombre del usuario
            });
            setReciboData(prev => ({ ...prev, numeroRecibo: newReciboNum }));
            setIsEditing(true);
            setMessage(`✅ Recibo #${newReciboNum} guardado exitosamente.`);
            await fetchAllRecibos();
        }
    } catch (err) {
        console.error("Error al guardar el recibo: ", err);
        setError(`Error al guardar: ${err.message}`);
        alert(`Error al guardar el recibo: ${err.message}`);
    } finally {
        setLoading(false);
    }
};
    const handleNew = () => {
        setReciboData({
            numeroRecibo: '',
            fechaRecibo: new Date(),
            cliente: '',
            moneda: 'Colones',
            monto: 0,
            cancelaCuenta: false,
            montoEnLetras: '',
            concepto: '',
            formaDePago: 'Sinpe Móvil',
            banco: '',
            numeroDeDocumento: '',
            saldoAnterior: 0,
            esteAbono: 0,
            saldoActual: 0,
            hechoPor: currentUserName || '', // Usar el nombre del usuario
        });
        setIsEditing(false);
        setError('');
        setMessage('');
        setSearchTerm('');
    };

    const handleSearch = async () => {
        const term = searchTerm.trim();
        if (!term) return;

        setLoading(true);
        setError('');
        setMessage('');

        try {
            // Try searching by numeroRecibo first (exact match)
            if (/^\d+$/.test(term)) {
                await loadRecibo(term.padStart(4, '0'));
                setLoading(false);
                return;
            }

            // If not a number, search by client name (partial match)
            // If not a number, search by client name (partial match) using the local list
            const results = allRecibos
                .filter(recibo => 
                    recibo.cliente?.toLowerCase().includes(term.toLowerCase())
                )
                .sort((a, b) => {
                    const numA = parseInt(a.numeroRecibo) || 0;
                    const numB = parseInt(b.numeroRecibo) || 0;
                    return numB - numA; // More recent first
                });

            if (results.length === 0) {
                alert(`No se encontraron recibos para "${term}"`);
            } else if (results.length === 1) {
                await loadRecibo(results[0].numeroRecibo);
            } else {
                setSearchResults(results);
                setIsSearchModalOpen(true);
            }
        } catch (err) {
            console.error("Error al buscar recibos:", err);
            setError(`Error al buscar: ${err.message}`);
        } finally {
            setLoading(false);
        }
    };

    const navigateRecibo = (direction) => {
        if (allRecibos.length === 0) {
            alert("No hay recibos disponibles para navegar");
            return;
        }

        const currentNum = reciboData.numeroRecibo;
        if (!currentNum) {
            const target = direction === 'prev' ? allRecibos[allRecibos.length - 1] : allRecibos[0];
            loadRecibo(target.numeroRecibo);
            return;
        }

        const currentIndex = allRecibos.findIndex(r => r.numeroRecibo === currentNum);
        if (currentIndex === -1) {
            alert("No se encontró el recibo actual en la lista");
            return;
        }

        let newIndex;
        if (direction === 'prev') {
            newIndex = currentIndex - 1;
            if (newIndex < 0) {
                alert("Ya estás en el primer recibo");
                return;
            }
        } else {
            newIndex = currentIndex + 1;
            if (newIndex >= allRecibos.length) {
                alert("Ya estás en el último recibo");
                return;
            }
        }
        loadRecibo(allRecibos[newIndex].numeroRecibo);
    };

    if (loading && !reciboData.numeroRecibo) {
        return <div className="loading-container"><div className="loading-spinner"></div><p>Cargando...</p></div>;
    }

    return (
        <>
            {isClienteModalOpen && (
                <ClienteSearchModal
                    onClose={() => setIsClienteModalOpen(false)}
                    onSelect={handleClienteSelect}
                    isAdmin={isAdmin}
                />
            )}

            {isSearchModalOpen && (
                <div className="modal-overlay" onClick={() => setIsSearchModalOpen(false)}>
                    <div className="modal-content" onClick={e => e.stopPropagation()} style={{ width: '80%', maxWidth: '700px' }}>
                        <div className="modal-header">
                            <h2>Resultados de Búsqueda</h2>
                            <button onClick={() => setIsSearchModalOpen(false)} className="modal-close">×</button>
                        </div>
                        <div className="modal-body">
                            <div className="table-responsive" style={{ maxHeight: '400px' }}>
                                <table className="usuarios-table" style={{ width: '100%' }}>
                                    <thead>
                                        <tr>
                                            <th style={{ width: '20%' }}># Recibo</th>
                                            <th style={{ width: '50%' }}>Cliente</th>
                                            <th style={{ width: '20%' }}>Monto</th>
                                            <th style={{ width: '10%' }}>Acción</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {searchResults.map((recibo) => {
                                            const currencySymbol = recibo.moneda === 'Dólares' ? '$' : '₡';
                                            return (
                                                <tr key={recibo.id}>
                                                    <td>{recibo.numeroRecibo}</td>
                                                    <td>{recibo.cliente}</td>
                                                    <td style={{ textAlign: 'right' }}>
                                                        {currencySymbol}{(recibo.monto || 0).toLocaleString('en-US', {
                                                            minimumFractionDigits: 2,
                                                            maximumFractionDigits: 2
                                                        })}
                                                    </td>
                                                    <td>
                                                        <button
                                                            className="icon-btn success"
                                                            onClick={() => {
                                                                loadRecibo(recibo.numeroRecibo);
                                                                setIsSearchModalOpen(false);
                                                            }}
                                                            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '5px' }}
                                                        >
                                                            ✅
                                                        </button>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Barra Sticky */}
            <div className={"sticky-toolbar" + (isToolbarFixed ? " fixed" : "")} ref={toolbarRef} style={isToolbarFixed ? { top: headerHeight } : undefined}>
                <div className="sticky-toolbar__inner">
                    <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flex: 1 }}>
                        <div className="tooltip-container">
                            <button 
                                className="icon-btn" 
                                onClick={() => navigateRecibo('prev')} 
                                disabled={loading || !reciboData.numeroRecibo}
                                style={{ backgroundColor: '#17a2b8', color: 'white' }}
                            >
                                ⬅️
                            </button>
                            <span className="tooltip-text">Recibo Anterior</span>
                        </div>
                        <div className="numero-cotizacion" style={{ margin: 0 }}>
                            <strong style={{ fontSize: '1.2rem', color: '#333' }}>🧾 Recibo #: {reciboData.numeroRecibo || '00000'}</strong>
                        </div>
                        <div className="tooltip-container">
                            <button 
                                className="icon-btn" 
                                onClick={() => navigateRecibo('next')} 
                                disabled={loading || !reciboData.numeroRecibo}
                                style={{ backgroundColor: '#17a2b8', color: 'white' }}
                            >
                                ➡️
                            </button>
                            <span className="tooltip-text">Recibo Siguiente</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginLeft: '1rem' }}>
                            <input
                                type="text"
                                placeholder="Buscar por # o cliente..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                                style={{ 
                                    padding: '0.4rem 0.75rem', 
                                    border: '1px solid #ced4da', 
                                    borderRadius: '4px',
                                    fontSize: '0.9rem',
                                    width: '200px'
                                }}
                            />
                            <div className="tooltip-container">
                                <button 
                                    className="icon-btn" 
                                    onClick={handleSearch} 
                                    disabled={loading || !searchTerm.trim()}
                                    style={{ backgroundColor: '#ffc107', color: '#333' }}
                                >
                                    🔍
                                </button>
                                <span className="tooltip-text">Buscar</span>
                            </div>
                        </div>
                    </div>
                    <div className="toolbar-buttons">
                        <div className="tooltip-container">
                            <button className="icon-btn" onClick={handleNew} disabled={loading} style={{ backgroundColor: '#28a745', color: 'white' }}>
                                📄
                            </button>
                            <span className="tooltip-text">Nuevo Recibo</span>
                        </div>
                        <div className="tooltip-container">
                            <button className="icon-btn" onClick={handleSave} disabled={loading} style={{ backgroundColor: '#007bff', color: 'white' }}>
                                💾
                            </button>
                            <span className="tooltip-text">{isEditing ? 'Actualizar Recibo' : 'Guardar Recibo'}</span>
                        </div>
                        <div className="tooltip-container">
                            <button 
                                className="icon-btn" 
                                onClick={async () => {
                                    if (!reciboData.numeroRecibo) {
                                        alert('Debe guardar el recibo primero para generar el reporte.');
                                        return;
                                    }
                                    await abrirReporteRecibo(reciboData.numeroRecibo);
                                }} 
                                disabled={loading || !reciboData.numeroRecibo} 
                                style={{ backgroundColor: '#6c757d', color: 'white' }}
                            >
                                🖨️
                            </button>
                            <span className="tooltip-text">Imprimir Recibo</span>
                        </div>

                    </div>
                </div>
            </div>
            {/* Espaciador para evitar saltos cuando se fija */}
            <div className={"sticky-toolbar__spacer" + (isToolbarFixed ? " active" : "")} style={{ height: isToolbarFixed ? toolbarHeight : 0 }}></div>

            <div className="gestion-usuarios-container cotizaciones-container">
                <div className="cotizacion-layout-vertical">
                    {error && <div className="alert alert-danger">{error}</div>}
                    {message && <div className="alert alert-success">{message}</div>}

                    <div className="panel-encabezado">
                        <div className="card">
                            <div className="card-body">
                                <div className="seccion-encabezado">
                                    <div className="form-grid">
                                        <div className="form-row form-row-half">
                                            <label className="form-label">Fecha:</label>
                                            <div className="fecha-display">
                                                {reciboData.fechaRecibo instanceof Date 
                                                ? `${getTodayFormatted()}`
                                                : getTodayFormatted()
                                                }
                                            </div>
                                        </div>
                                        
                                        <div className="form-row form-row-full">
                                            <label className="form-label">Cliente:</label>
                                            <div style={{ display: 'flex', flex: '1 1 auto', alignItems: 'center' }}>
                                                <input
                                                    type="text"
                                                    name="cliente"
                                                    value={reciboData.cliente}
                                                    onChange={handleInputChange}
                                                    className="form-control"
                                                    readOnly
                                                    style={{ backgroundColor: '#f8f9fa', cursor: 'not-allowed', width: '100%' }}
                                                />
                                                <div className="tooltip-container" style={{ marginLeft: '8px' }}>
                                                    <button
                                                        className="icon-btn"
                                                        type="button"
                                                        onClick={() => setIsClienteModalOpen(true)}
                                                        style={{ 
                                                            backgroundColor: '#bccbd8ff', 
                                                            color: 'white',
                                                            fontSize: '1.1rem'
                                                        }}
                                                    >
                                                        👨‍💼
                                                    </button>
                                                    <span className="tooltip-text">Seleccionar cliente</span>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="form-row form-row-half">
                                            <label className="form-label">Monto:</label>
                                            <div style={{ flex: '0 1 12.5%' }}>
                                                <input 
                                                    type="number" 
                                                    name="monto" 
                                                    value={reciboData.monto} 
                                                    onChange={handleInputChange} 
                                                    className="form-control" 
                                                    step="0.01"
                                                    style={{ textAlign: 'right' }}
                                                />
                                            </div>
                                        </div>
                                        <div className="form-row form-row-half">
                                            <label className="form-label">Moneda:</label>
                                            <select
                                                name="moneda" 
                                                value={reciboData.moneda} 
                                                onChange={handleInputChange} 
                                                className="form-select"
                                            >
                                                <option value="Colones">Colones</option>
                                                <option value="Dólares">Dólares</option>
                                            </select>
                                        </div>

                                        <div className="form-row form-row-full">
                                            <label className="form-label">Monto en Letras:</label>
                                            <input 
                                                type="text" 
                                                name="montoEnLetras" 
                                                value={reciboData.montoEnLetras} 
                                                onChange={handleInputChange} 
                                                className="form-control" 
                                                readOnly
                                                style={{ backgroundColor: '#f8f9fa', cursor: 'not-allowed' }}
                                            />
                                        </div>

                                        <div className="form-row form-row-full">
                                            <label className="form-label">Concepto:</label>
                                            <textarea
                                                name="concepto"
                                                value={reciboData.concepto}
                                                onChange={handleInputChange}
                                                className="form-control"
                                                rows="2"
                                            />
                                        </div>

                                        <div className="form-row form-row-half">
                                            <label className="form-label">Forma de Pago:</label>
                                            <select 
                                                name="formaDePago" 
                                                value={reciboData.formaDePago} 
                                                onChange={handleInputChange} 
                                                className="form-select"
                                            >
                                                <option value="Sinpe Móvil">Sinpe Móvil</option>
                                                <option value="Efectivo">Efectivo</option>
                                                <option value="Transferencia">Transferencia</option>
                                                <option value="Tarjeta">Tarjeta</option>
                                                <option value="Cheque">Cheque</option>
                                                <option value="Otro">Otro</option>
                                            </select>
                                        </div>
                                        <div className="form-row form-row-half">
                                            <label className="form-label">Banco:</label>
                                            <input 
                                                type="text" 
                                                name="banco" 
                                                value={reciboData.banco} 
                                                onChange={handleInputChange} 
                                                className="form-control" 
                                            />
                                        </div>

                                        <div className="form-row form-row-full">
                                            <label className="form-label"># Documento:</label>
                                            <input 
                                                type="text" 
                                                name="numeroDeDocumento" 
                                                value={reciboData.numeroDeDocumento} 
                                                onChange={handleInputChange} 
                                                className="form-control" 
                                            />
                                        </div>

                                        <div className="form-row form-row-half">
                                            <label className="form-label">Saldo Anterior:</label>
                                            <div style={{ flex: '0 1 12.5%' }}>
                                                <input 
                                                    type="number" 
                                                    name="saldoAnterior" 
                                                    value={reciboData.saldoAnterior} 
                                                    onChange={handleInputChange} 
                                                    className="form-control" 
                                                    step="0.01"
                                                    style={{ textAlign: 'right' }}
                                                />
                                            </div>
                                        </div>
                                        <div className="form-row form-row-half">
                                            <label className="form-label">Este Abono:</label>
                                            <div style={{ flex: '0 1 12.5%' }}>
                                                <input 
                                                    type="number" 
                                                    name="esteAbono" 
                                                    value={reciboData.esteAbono} 
                                                    onChange={handleInputChange} 
                                                    className="form-control" 
                                                    step="0.01"
                                                    style={{ textAlign: 'right', backgroundColor: '#f8f9fa', cursor: 'not-allowed' }}
                                                    readOnly
                                                />
                                            </div>
                                        </div>
                                        <div className="form-row form-row-half">
                                            <label className="form-label">Saldo Actual:</label>
                                            <div style={{ flex: '0 1 12.5%' }}>
                                                <input 
                                                    type="number" 
                                                    name="saldoActual" 
                                                    value={reciboData.saldoActual} 
                                                    className="form-control" 
                                                    readOnly
                                                    style={{ backgroundColor: '#f8f9fa', cursor: 'not-allowed', textAlign: 'right' }}
                                                />
                                            </div>
                                        </div>

                                        <div className="form-row form-row-full">
                                            <label className="form-label">Hecho Por:</label>
                                            <input 
                                                type="text" 
                                                name="hechoPor" 
                                                value={reciboData.hechoPor} 
                                                className="form-control" 
                                                readOnly
                                                style={{ backgroundColor: '#f8f9fa', cursor: 'not-allowed' }}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default SicRecibos;